import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import type {
  Payment,
  PaymentMethod,
  PaymentRequirementTarget,
} from '@pta/types';
import { createPayment, updatePayment } from '../api/payments';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';
import { useMoneyFormatter } from '../util/money';

interface PaymentFormModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  target: PaymentRequirementTarget;
  studentName: string;
  /** A csoport / requirement primary currency-je (display fallback). */
  currency: string;
  paidSoFar: string;
  payment?: Payment | null;
  onSaved: () => void;
}

const PAYMENT_METHODS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Készpénz' },
  { value: 'bank_transfer', label: 'Banki átutalás' },
  { value: 'card', label: 'Kártya' },
  { value: 'escrow', label: 'Letét' },
];

const fullSpan = { gridColumn: '1 / -1' };

/**
 * F2e — Payment create/edit modal (FE).
 * Layout: mobilon 1 oszlop, desktopon (sm+) 2 oszlop.
 */
export function PaymentFormModal({
  opened,
  onClose,
  groupId,
  target,
  studentName,
  currency,
  paidSoFar,
  payment,
  onSaved,
}: PaymentFormModalProps) {
  const { money, currencyLabel } = useMoneyFormatter();
  const isEdit = payment != null;

  const [amount, setAmount] = useState<number | ''>('');
  const [paidAt, setPaidAt] = useState<Date | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('bank_transfer');
  const [externalRef, setExternalRef] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const expected = Number(target.expectedAmount);
  const paid = Number(paidSoFar);
  const remaining = Math.max(0, expected - paid);

  useEffect(() => {
    if (!opened) return;
    if (isEdit && payment) {
      setAmount(Number(payment.amount));
      setPaidAt(new Date(payment.paidAt));
      setPaymentMethod(payment.paymentMethod);
      setExternalRef(payment.externalRef ?? '');
      setNotes(payment.notes ?? '');
    } else {
      setAmount('');
      setPaidAt(new Date());
      setPaymentMethod('bank_transfer');
      setExternalRef('');
      setNotes('');
    }
  }, [opened, isEdit, payment]);

  const canSave =
    typeof amount === 'number' && amount > 0 && paidAt !== null && !saving;

  const handleSave = async () => {
    if (!canSave || typeof amount !== 'number' || !paidAt) return;
    setSaving(true);
    try {
      const amountStr = amount.toFixed(2).replace(/\.00$/, '');
      if (isEdit && payment) {
        await updatePayment(payment.id, {
          amount: amountStr,
          paidAt: toIsoDate(paidAt),
          paymentMethod,
          externalRef: externalRef.trim() || null,
          notes: notes.trim() || null,
        });
        notifications.show({ color: 'green', message: 'Befizetés frissítve.' });
      } else {
        await createPayment(groupId, {
          targetId: target.id,
          amount: amountStr,
          paidAt: toIsoDate(paidAt),
          paymentMethod,
          externalRef: externalRef.trim() || null,
          notes: notes.trim() || null,
        });
        notifications.show({ color: 'green', message: 'Befizetés rögzítve.' });
      }
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const labelForInput = currencyLabel || currency;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Befizetés szerkesztése' : 'Új befizetés rögzítése'}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Stack gap={2}>
            <Text size="sm">
              <strong>Diák:</strong> {studentName}
            </Text>
            <Text size="sm">
              <strong>Várt összeg:</strong> {money(target.expectedAmount)}
            </Text>
            <Group gap="xs">
              <Text size="sm">
                <strong>Eddig fizetve:</strong> {money(paidSoFar)}
              </Text>
              {remaining > 0 ? (
                <Badge color="orange" variant="light">
                  Hátra: {money(remaining)}
                </Badge>
              ) : (
                <Badge color="teal" variant="light">
                  Teljesítve
                </Badge>
              )}
            </Group>
          </Stack>
        </Alert>

        {!isEdit && (
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              onClick={() => setAmount(remaining > 0 ? remaining : expected)}
            >
              {remaining > 0
                ? `Teljes hátralék (${money(remaining)})`
                : `Teljes várt (${money(expected)})`}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setAmount(expected / 2)}
            >
              Fele ({money(expected / 2)})
            </Button>
          </Group>
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <NumberInput
            label={`Összeg (${labelForInput})`}
            value={amount}
            onChange={(v) => setAmount(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />

          <DateInput
            label="Fizetés dátuma"
            value={paidAt}
            onChange={(v) => setPaidAt(v ? new Date(v) : null)}
            valueFormat="YYYY-MM-DD"
            required
          />

          <Select
            style={fullSpan}
            label="Fizetési mód"
            data={PAYMENT_METHODS}
            value={paymentMethod}
            onChange={(v) => setPaymentMethod((v as PaymentMethod) ?? 'bank_transfer')}
            allowDeselect={false}
            required
          />

          <Textarea
            style={fullSpan}
            label="Külső hivatkozás (banki tranzakció azonosító, opcionális)"
            value={externalRef}
            onChange={(e) => setExternalRef(e.currentTarget.value)}
            autosize
            minRows={1}
            maxLength={200}
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            {isEdit ? 'Mentés' : 'Rögzítés'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
